'use client';

import { useCallback, useMemo } from 'react';

import { parseAsArrayOf, parseAsBoolean, parseAsString, parseAsStringEnum, useQueryStates } from 'nuqs';

import type { DimensionsValue, UtmValue, WidgetConfigState } from '../fields';
import type { WidgetSchema } from '../types';

/**
 * Holds widget config state, URL-synced via nuqs so a configured widget is a
 * shareable/bookmarkable link. Object-valued fields are flattened into clean
 * query params (dimensions -> width/height; utm -> utm_source/utm_campaign/referral),
 * then reassembled into the `{ config, setField }` shape the field components and
 * embed generator expect (so nothing downstream changes). Defaults are omitted
 * from the URL (`clearOnDefault`) and updates use `replace` history (no spam).
 */
export function useWidgetConfig(schema: WidgetSchema): {
    config: WidgetConfigState;
    setField: (key: string, value: unknown) => void;
} {
    const parsers = useMemo<Record<string, any>>(() => {
        const map: Record<string, unknown> = {};
        for (const field of schema.fields) {
            switch (field.kind) {
                case 'theme':
                    map[field.key] = parseAsStringEnum(['light', 'dark']).withDefault(field.defaultValue);
                    break;
                case 'boolean':
                    map[field.key] = parseAsBoolean.withDefault(field.defaultValue);
                    break;
                case 'market-select':
                    map[field.key] = parseAsArrayOf(parseAsString).withDefault(field.defaultValue);
                    break;
                case 'dimensions':
                    map.width = parseAsString.withDefault(field.defaultValue.width);
                    map.height = parseAsString.withDefault(field.defaultValue.height);
                    break;
                case 'utm':
                    map.utm_source = parseAsString.withDefault(field.defaultValue.utmSource);
                    map.utm_campaign = parseAsString.withDefault(field.defaultValue.utmCampaign);
                    map.referral = parseAsString.withDefault(field.defaultValue.promoCode);
                    break;
                default:
                    map[field.key] = parseAsString.withDefault(String(field.defaultValue ?? ''));
            }
        }
        return map;
    }, [schema]);

    const [flat, setFlat] = useQueryStates(parsers, { history: 'replace', clearOnDefault: true });

    const config = useMemo<WidgetConfigState>(() => {
        const result: WidgetConfigState = {};
        for (const field of schema.fields) {
            if (field.kind === 'dimensions') {
                result[field.key] = { width: flat.width, height: flat.height } satisfies DimensionsValue;
            } else if (field.kind === 'utm') {
                result[field.key] = {
                    utmSource: flat.utm_source,
                    utmCampaign: flat.utm_campaign,
                    promoCode: flat.referral,
                } satisfies UtmValue;
            } else {
                result[field.key] = flat[field.key];
            }
        }
        return result;
    }, [schema, flat]);

    const setField = useCallback(
        (key: string, value: unknown) => {
            const field = schema.fields.find((f) => f.key === key);
            if (!field) return;
            if (field.kind === 'dimensions') {
                const dims = value as DimensionsValue;
                setFlat({ width: dims.width, height: dims.height });
            } else if (field.kind === 'utm') {
                const utm = value as UtmValue;
                setFlat({ utm_source: utm.utmSource, utm_campaign: utm.utmCampaign, referral: utm.promoCode });
            } else {
                setFlat({ [key]: value });
            }
        },
        [schema, setFlat],
    );

    return { config, setField };
}
